import React from "react";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import { capitalizeFunction } from "@features/vessel-call/vessel-call-status/VesselCallDetails/utils.tsx";
import { getVesselCategory } from "@utils/vessel-category";
import { t } from "i18next";
import type { VesselInformationDTO } from "../VesselCallStatus.types";

const TEST_ID_BASE = "vessel-call-status-page";
const LANG_BASE = "vessel-call";
const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3";
const wrapperFirstStyleClass = "pt-3";
const transStyleClass = "font-normal text-[#646A77]";

const VesselInformation: React.FC<{ vesselDetails?: VesselInformationDTO | null }> = ({
  vesselDetails,
}) => {
  return (
    <div data-testid={`${TEST_ID_BASE}`} className="flex flex-col w-full h-full p-8 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Paper variant="outlined">
          <div data-testid={`${TEST_ID_BASE}-section-vessel-details`}>
            <p className="font-bold">{t(`${LANG_BASE}.vessel-information-view.vessel-details`)}</p>
            <PaperItem
              testId={`${TEST_ID_BASE}-imo-number`}
              translate={t(`${LANG_BASE}.vessel-information-view.imo-number`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperFirstStyleClass}
              text={vesselDetails?.imoNumber || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-vessel-name`}
              translate={t(`${LANG_BASE}.vessel-information-view.vessel-name`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.vesselName || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-vessel-call-sign`}
              translate={t(`${LANG_BASE}.vessel-information-view.call-sign`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.vesselCallSign || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-vessel-build-year`}
              translate={t(`${LANG_BASE}.vessel-information-view.ship-build-year`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.shipBuildYear || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-vessel-type`}
              translate={t(`${LANG_BASE}.vessel-information-view.vessel-type`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.vesselType || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-port-of-registration`}
              translate={t(`${LANG_BASE}.vessel-information-view.port-of-registration`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.portName || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-flag`}
              translate={t(`${LANG_BASE}.vessel-information-view.flag`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.countryCode || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-vessel-category`}
              translate={t(`${LANG_BASE}.vessel-information-view.vessel-category-description`)}
              textStyles="font-semibold"
              tranStyle={`${transStyleClass} whitespace-pre-line`}
              wrapperStyle={wrapperStyleClass}
              text="-" // Vessel category will be decided by MD when approving the registration. That functionality is not developed yet, so leave this info as blank (to be populated in later phase)
            />
          </div>
        </Paper>

        <Paper variant="outlined">
          <div data-testid={`${TEST_ID_BASE}-section-technical-details`}>
            <p className="font-bold">
              {t(`${LANG_BASE}.vessel-information-view.vessel-particulars`)}
            </p>
            <PaperItem
              testId={`${TEST_ID_BASE}-gt`}
              translate={t(`${LANG_BASE}.vessel-information-view.gt`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperFirstStyleClass}
              text={vesselDetails?.grt != null ? vesselDetails.grt + " Tons" : "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-nt`}
              translate={t(`${LANG_BASE}.vessel-information-view.nt`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.nrt != null ? vesselDetails.nrt + " Tons" : "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-dwt`}
              translate={t(`${LANG_BASE}.vessel-information-view.dwt`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.dwt != null ? vesselDetails.dwt + " Tons" : "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-category`}
              translate={t(`${LANG_BASE}.vessel-information-view.vessel-category`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.dwt != null ? getVesselCategory(vesselDetails?.dwt) : "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-loa`}
              translate={t(`${LANG_BASE}.vessel-information-view.loa`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.loa != null ? vesselDetails.loa + " Meters" : "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-beam`}
              translate={t(`${LANG_BASE}.vessel-information-view.beam`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.beam != null ? vesselDetails.beam + " Meters" : "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-vessel-classification-society`}
              translate={t(`${LANG_BASE}.vessel-information-view.vessel-classification-society`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={capitalizeFunction(vesselDetails?.classDesc) || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-max-draft`}
              translate={t(`${LANG_BASE}.vessel-information-view.max-draft`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.draftMax != null ? vesselDetails.draftMax + " Meters" : "-"}
            />
            <div className="min-h-[4.4rem]"></div>
          </div>
        </Paper>

        <Paper variant="outlined">
          <div data-testid={`${TEST_ID_BASE}-section-ownership`}>
            <p className="font-bold">
              {t(`${LANG_BASE}.vessel-information-view.vessel-ownership`)}
            </p>
            <PaperItem
              testId={`${TEST_ID_BASE}-ship-ownership`}
              translate={t(`${LANG_BASE}.vessel-information-view.ship-ownership`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperFirstStyleClass}
              text={vesselDetails?.shipsOwner || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-ship-charterer`}
              translate={t(`${LANG_BASE}.vessel-information-view.ship-charterer`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.shipsCharterer || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-ships-management`}
              translate={t(`${LANG_BASE}.vessel-information-view.ships-management`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.shipsManagement || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-ships-registered-owner`}
              translate={t(`${LANG_BASE}.vessel-information-view.ships-registered-owner`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.shipsRegisteredOwner || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-name-of-pi-club`}
              translate={t(`${LANG_BASE}.vessel-information-view.name-of-pi-club`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.piClubName || "-"}
            />
            <PaperItem
              testId={`${TEST_ID_BASE}-mlc-of-pi-club`}
              translate={t(`${LANG_BASE}.vessel-information-view.mlc-of-pi-club`)}
              textStyles="font-semibold"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={vesselDetails?.mlcPiClubName || "-"}
            />
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default VesselInformation;
